<template>
  <div class="home-shell">
    <section class="panel mb-6">
      <h1 class="page-title">Events</h1>
      <p class="page-subtitle">Prepare your ceremony, then hand over to the team running it.</p>

      <div v-if="canCreateEvents" class="create-grid">
        <div>
          <label class="field-label">Event Name</label>
          <input
            v-model.trim="newEvent.name"
            type="text"
            class="field-input"
            placeholder="Example Awards 2026"
            @keyup.enter="createAndOpenSetup"
          />
        </div>
        <div class="create-action">
          <button class="primary-button" @click="createAndOpenSetup" :disabled="creatingEvent">
            {{ creatingEvent ? "Creating..." : "Create Event" }}
          </button>
        </div>
      </div>
    </section>

    <section class="panel">
      <div class="section-header">
        <div>
          <h2 class="section-title">All Events</h2>
          <p class="section-copy">Open a ceremony to see what needs attention.</p>
        </div>
        <button class="secondary-button" @click="loadEvents">Refresh</button>
      </div>

      <p v-if="error" class="notice error">{{ error }}</p>
      <p v-if="loading" class="empty-copy">Loading events...</p>
      <p v-else-if="events.length === 0" class="empty-copy">No events found yet.</p>

      <div v-else class="events-list">
        <article
          v-for="event in events"
          :key="event.eventId"
          class="event-card"
        >
          <div class="event-copy">
            <h3 class="event-name">{{ event.name }}</h3>
            <p class="event-meta">Reference: {{ event.slug || event.eventId }}</p>
            <p class="event-meta">Status: {{ event.status }}</p>
            <p class="event-meta">Workflow: {{ event.workflowStatus }}</p>
            <p class="event-meta">Participants: {{ event.participantsCount }} · Queue: {{ event.queueCount }}</p>
          </div>

          <div class="event-actions">
            <router-link :to="`/events/${encodeURIComponent(event.eventId)}/overview`" class="action-link primary-link">Open ceremony</router-link>
            <router-link v-if="canSeeParticipants" :to="participantsPath(event.eventId)" class="action-link neutral-link">Participants</router-link>
            <router-link v-if="canSeeOratorPrep" :to="oratorPrepPath(event.eventId)" class="action-link neutral-link">Orator Prep</router-link>
            <router-link v-if="canSeeViews" :to="viewsPath(event.eventId)" class="action-link neutral-link">Views</router-link>
            <router-link v-if="canSeeQueue" :to="queuePath(event.eventId)" class="action-link neutral-link">Queue</router-link>
            <router-link v-if="canSeePresenter" :to="presenterPath(event.eventId)" target="_blank" rel="noopener noreferrer" title="Opens in a new tab" class="action-link neutral-link">Presenter</router-link>
            <router-link v-if="canSeeLowerThirds" :to="lowerThirdsPath(event.eventId)" target="_blank" rel="noopener noreferrer" title="Opens in a new tab" class="action-link neutral-link">Lower Thirds</router-link>
            <button
              v-if="canArchiveEvents && event.status !== 'archived'"
              class="icon-action danger-icon-button"
              @click="archiveSelectedEvent(event)"
              :disabled="archivingEventId === event.eventId"
              :aria-label="`Archive ${event.name}`"
              :title="archivingEventId === event.eventId ? 'Archiving event' : 'Archive event'"
            >
              <svg
                aria-hidden="true"
                class="archive-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M21 8v13H3V8" />
                <path d="M1 3h22v5H1z" />
                <path d="M10 12h4" />
              </svg>
            </button>
          </div>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
import { archiveEvent, createEvent, fetchEvents } from "@/lib/api";
import { hasAnyRole } from "@/lib/auth";

export default {
  name: "EventHome",
  data() {
    return {
      events: [],
      loading: false,
      creatingEvent: false,
      archivingEventId: "",
      error: "",
      newEvent: {
        name: "",
      },
    };
  },
  computed: {
    canCreateEvents() {
      return hasAnyRole(["internal_admin", "uni_admin"]);
    },
    canArchiveEvents() {
      return hasAnyRole(["internal_admin"]);
    },
    canSeeSetup() {
      return hasAnyRole(["internal_admin", "uni_admin"]);
    },
    canSeeParticipants() {
      return hasAnyRole(["internal_admin", "uni_admin", "operator"]);
    },
    canSeeOratorPrep() {
      return hasAnyRole(["internal_admin", "uni_admin", "orator"]);
    },
    canSeeViews() {
      return hasAnyRole(["internal_admin", "uni_admin", "av"]);
    },
    canSeeQueue() {
      return hasAnyRole(["internal_admin", "uni_admin", "operator"]);
    },
    canSeePresenter() {
      return hasAnyRole(["internal_admin", "uni_admin", "operator", "orator"]);
    },
    canSeeLowerThirds() {
      return hasAnyRole(["internal_admin", "uni_admin", "operator", "av"]);
    },
  },
  methods: {
    async loadEvents() {
      this.loading = true;
      this.error = "";
      try {
        this.events = await fetchEvents();
      } catch (error) {
        this.error = "Unable to load events.";
      } finally {
        this.loading = false;
      }
    },
    async createAndOpenSetup() {
      if (!this.newEvent.name) {
        this.error = "Enter an event name first.";
        return;
      }
      this.creatingEvent = true;
      this.error = "";
      try {
        const snapshot = await createEvent({ name: this.newEvent.name });
        const eventId = snapshot.event?.eventId;
        if (!eventId) {
          throw new Error("Missing event ID");
        }
        this.newEvent.name = "";
        this.$router.push({ path: this.setupPath(eventId) });
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to create event.";
      } finally {
        this.creatingEvent = false;
      }
    },
    async archiveSelectedEvent(event) {
      if (!event?.eventId) {
        return;
      }
      this.archivingEventId = event.eventId;
      this.error = "";
      try {
        await archiveEvent(event.eventId);
        await this.loadEvents();
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to archive event.";
      } finally {
        this.archivingEventId = "";
      }
    },
    setupPath(eventId) {
      return `/events/${encodeURIComponent(eventId)}/setup`;
    },
    participantsPath(eventId) {
      return `/events/${encodeURIComponent(eventId)}/participants`;
    },
    oratorPrepPath(eventId) {
      return `/events/${encodeURIComponent(eventId)}/orator-prep`;
    },
    viewsPath(eventId) {
      return `/events/${encodeURIComponent(eventId)}/views`;
    },
    queuePath(eventId) {
      return `/events/${encodeURIComponent(eventId)}/queue`;
    },
    presenterPath(eventId) {
      return `/events/${encodeURIComponent(eventId)}/presenter`;
    },
    lowerThirdsPath(eventId) {
      return `/events/${encodeURIComponent(eventId)}/lower-thirds`;
    },
  },
  mounted() {
    this.loadEvents();
  },
};
</script>

<style scoped>
.home-shell {
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 1rem 2.5rem;
}

.panel {
  background: white;
  border: 1px solid var(--orator-smoke);
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.06);
  padding: 1.5rem;
}

.page-title,
.section-title {
  font-size: 2rem;
  font-weight: 900;
  color: var(--orator-navy);
}

.page-subtitle,
.section-copy,
.event-meta,
.empty-copy {
  margin-top: 0.35rem;
  color: var(--orator-graphite);
  font-weight: 500;
}

.create-grid {
  margin-top: 1.5rem;
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(0, 1fr) auto;
}

.create-action {
  display: flex;
  align-items: flex-end;
}

@media (max-width: 640px) {
  .create-grid {
    grid-template-columns: 1fr;
  }

  .create-action,
  .primary-button {
    width: 100%;
  }
}

.field-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--orator-arsenic);
  margin-bottom: 0.5rem;
}

.field-input {
  display: block;
  width: 100%;
  border-radius: 0.45rem;
  border: 1px solid var(--orator-steel);
  padding: 0.625rem 0.75rem;
  background: white;
}

.section-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  flex-wrap: wrap;
}

.events-list {
  margin-top: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.event-card {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  border: 1px solid var(--orator-smoke);
  border-radius: 0.75rem;
  padding: 1rem;
}

.event-name {
  font-size: 1.2rem;
  font-weight: 800;
  color: var(--orator-navy);
}

.event-actions {
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.primary-button,
.secondary-button,
.action-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.625rem 0.95rem;
  border-radius: 0.45rem;
  font-weight: 700;
  text-decoration: none;
  border: 1px solid var(--orator-steel);
}

.icon-action {
  width: 2.55rem;
  height: 2.55rem;
  padding: 0;
  border-radius: 0.45rem;
  border: 1px solid var(--orator-steel);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: white;
  cursor: pointer;
}

button.action-link {
  cursor: pointer;
}

button.action-link:disabled,
.icon-action:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

.primary-button,
.primary-link {
  background: var(--orator-blue);
  border-color: var(--orator-blue);
  color: white;
}

.secondary-button,
.neutral-link {
  background: white;
  color: var(--orator-arsenic);
}

.danger-icon-button {
  background: #fef2f2;
  border-color: #fecaca;
  color: #991b1b;
}

.archive-icon {
  width: 1.1rem;
  height: 1.1rem;
}

.notice {
  margin-top: 1rem;
  padding: 0.9rem 1rem;
  border-radius: 0.65rem;
  font-weight: 600;
}

.notice.error {
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}

@media (max-width: 900px) {
  .create-grid {
    grid-template-columns: 1fr;
  }

  .event-card {
    flex-direction: column;
  }

  .event-actions {
    justify-content: flex-start;
  }
}
</style>
